import { useState, type ReactNode } from 'react';
import { PiEyeBold, PiFilePdfBold } from 'react-icons/pi';
import { Badge, Button, LoadingState, cn } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import { useI18n } from '@v2/providers/I18nProvider';
import type { DealDocumentSummary, DealQuoteSummary } from '../types';
import { formatBrl, formatDateTime } from '../utils';
import DetailCard from './DetailCard';
import { useDealFieldSectionsExtras } from './PloomesDealFieldSectionsPanel';

type Mode = 'quotes' | 'documents';

type Props = {
    dealId: number;
    mode: Mode;
};

export type PreviewTarget = {
    title: string;
    url: string;
    subtitle?: string;
};

export default function PloomesDealDocumentsQuotesPanel({ dealId, mode }: Props) {
    const { t } = useI18n();
    const { data, isLoading, isError } = useDealFieldSectionsExtras(dealId);
    const [preview, setPreview] = useState<PreviewTarget | null>(null);

    if (isLoading) {
        return <LoadingState variant="cards" />;
    }

    if (isError || !data) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.fields_load_error')}
            </p>
        );
    }

    return (
        <>
            {mode === 'quotes' ? (
                <QuotesGrid quotes={data.quotes} onPreview={setPreview} />
            ) : (
                <DocumentsGrid documents={data.documents} onPreview={setPreview} />
            )}

            <PdfPreviewModal
                preview={preview}
                onClose={() => setPreview(null)}
            />
        </>
    );
}

function QuotesGrid({
    quotes,
    onPreview,
}: {
    quotes: DealQuoteSummary[];
    onPreview: (target: PreviewTarget) => void;
}) {
    const { t } = useI18n();

    if (quotes.length === 0) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.quotes_empty')}
            </p>
        );
    }

    return (
        <DetailCard title={t('v2.ploomes_deals.tabs.proposals')} icon={<PiFilePdfBold className="h-5 w-5" />}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {quotes.map((q) => {
                    const title = q.file_name || `Quote #${q.id}`;
                    const subtitle = [
                        formatDateTime(q.created_at),
                        q.amount != null ? formatBrl(q.amount) : null,
                    ]
                        .filter(Boolean)
                        .join(' · ');

                    return (
                        <PdfCard
                            key={q.id}
                            title={title}
                            subtitle={subtitle}
                            url={q.url}
                            highlight={Boolean(q.is_origin)}
                            badge={
                                q.is_origin ? (
                                    <Badge color="warning" size="sm" rounded="pill" variant="solid" title={t('v2.ploomes_deals.quote_origin_hint')}>
                                        {t('v2.ploomes_deals.quote_origin')}
                                    </Badge>
                                ) : q.last_review ? (
                                    <Badge color="success" size="sm" rounded="pill" variant="flat">
                                        Last review
                                    </Badge>
                                ) : null
                            }
                            onPreview={() => {
                                if (!q.url) return;
                                onPreview({ title, url: q.url, subtitle });
                            }}
                        />
                    );
                })}
            </div>
        </DetailCard>
    );
}

function DocumentsGrid({
    documents,
    onPreview,
}: {
    documents: DealDocumentSummary[];
    onPreview: (target: PreviewTarget) => void;
}) {
    const { t } = useI18n();

    if (documents.length === 0) {
        return (
            <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
                {t('v2.ploomes_deals.documents_empty')}
            </p>
        );
    }

    return (
        <DetailCard title={t('v2.ploomes_deals.tabs.documents')} icon={<PiFilePdfBold className="h-5 w-5" />}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {documents.map((d) => {
                    const title = d.name || d.file_name || `Doc #${d.id}`;
                    const subtitle = formatDateTime(d.created_at);

                    return (
                        <PdfCard
                            key={d.id}
                            title={title}
                            subtitle={subtitle}
                            url={d.url}
                            onPreview={() => {
                                if (!d.url) return;
                                onPreview({ title, url: d.url, subtitle });
                            }}
                        />
                    );
                })}
            </div>
        </DetailCard>
    );
}

function PdfCard({
    title,
    subtitle,
    url,
    badge,
    onPreview,
    highlight = false,
}: {
    title: string;
    subtitle?: string;
    url: string | null;
    badge?: ReactNode;
    onPreview: () => void;
    highlight?: boolean;
}) {
    const { t } = useI18n();
    const previewUrl = url ? `${url}${url.includes('#') ? '' : '#view=FitH'}` : null;
    const canOpen = Boolean(url);

    return (
        <article
            className={cn(
                'relative flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition dark:border-zinc-700 dark:bg-zinc-900',
                canOpen && !highlight && 'hover:border-orange-300 hover:shadow-md dark:hover:border-orange-500/40',
                highlight && 'border-2 border-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.18)] dark:border-amber-500',
            )}
        >
            {highlight && (
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-10 rounded-xl ring-4 ring-amber-400/60 motion-safe:animate-pulse"
                />
            )}
            <button
                type="button"
                disabled={!canOpen}
                onClick={onPreview}
                className="relative aspect-[3/4] bg-gray-100 text-left disabled:cursor-not-allowed dark:bg-zinc-950"
            >
                {previewUrl ? (
                    <iframe
                        title={title}
                        src={previewUrl}
                        className="pointer-events-none absolute inset-0 h-full w-full border-0"
                        loading="lazy"
                    />
                ) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400 dark:text-zinc-600">
                        <PiFilePdfBold className="size-12" />
                        <span className="text-xs">Sem preview</span>
                    </div>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/40 to-transparent" />
                {badge && <div className="absolute left-2 top-2 z-10">{badge}</div>}
            </button>

            <div className="flex flex-1 flex-col gap-2 p-3">
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-zinc-100" title={title}>
                        {title}
                    </p>
                    {subtitle && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{subtitle}</p>
                    )}
                </div>
                {canOpen ? (
                    <button
                        type="button"
                        onClick={onPreview}
                        className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 dark:border-zinc-700 dark:text-zinc-200 dark:hover:border-orange-500/40 dark:hover:bg-orange-950/30 dark:hover:text-orange-300"
                    >
                        <PiEyeBold className="size-3.5" />
                        {t('v2.ploomes_deals.relations_open')}
                    </button>
                ) : (
                    <span className="mt-auto text-center text-xs text-gray-400 dark:text-zinc-500">—</span>
                )}
            </div>
        </article>
    );
}

export function PdfPreviewModal({
    preview,
    onClose,
}: {
    preview: PreviewTarget | null;
    onClose: () => void;
}) {
    if (!preview) return null;

    const iframeSrc = `${preview.url}${preview.url.includes('#') ? '' : '#view=FitH'}`;

    return (
        <V2Modal
            open={Boolean(preview)}
            onClose={onClose}
            title={preview.title}
            description={preview.subtitle}
            size="5xl"
            footer={
                <Button type="button" onClick={onClose}>
                    Fechar
                </Button>
            }
        >
            <div className="-mx-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-100 dark:border-zinc-700 dark:bg-zinc-950">
                <iframe
                    title={preview.title}
                    src={iframeSrc}
                    className="h-[min(62vh,640px)] w-full border-0 bg-white dark:bg-zinc-950"
                />
            </div>
        </V2Modal>
    );
}
